import { useState, useContext } from "react";
import Modal from "../common/Modal";
import { v4 as uuid } from "uuid";
import firebase from "../../firebase/compat";
import { db } from "../../firebase/config";
import { client } from "../../hooks/Client";
import { Auth } from "../../StateManagment/Auth";
import { useFirestoreGeneralOrderBy } from "../../firebase/useFirestore";
import { useHistory } from "react-router-dom";
import ErasorIcon from "../../images/icons/erasor-icon.png";
import XIcon from "../../images/icons/x-icon.png";
import ReadOnlyGuard from "../common/ReadOnlyGuard";
import presenceReportDocId from "../../helpers/personas/presenceReportDocId";
import amsterdamDateKey from "../../helpers/personas/amsterdamDateKey";
import findSlotPresenceReport from "../../helpers/personas/findSlotPresenceReport";

/**
 * Correctie of afwezigheid vastleggen voor één roosterblok van één deelnemer,
 * vanuit de agenda.
 *
 * Schrijft naar hetzelfde vaste document als de nachtklus
 * (`presence_<roosterregel>_<datum>`), zodat er per blok per dag één rapport is:
 * wie het eerst is legt vast. Zonder roosterregel valt hij terug op een los
 * document; dat vangt de nachtklus af met manualSlotRegistration.
 *
 * AI-CONTEXT: See docs/pitfalls.md#database-job-dedups-only-on-its-own-docid
 *
 * @param {object} props
 * @param {string} [props.scheduleId] - Document-ID van de roosterregel (Task, Type personaSchedule)
 * @param {string} props.personaId - De deelnemer van deze roosterregel
 * @param {string} props.scheduledStart - Geroosterde begintijd ('HH:MM')
 * @param {string} props.scheduledEnd - Geroosterde eindtijd ('HH:MM')
 * @param {Date} [props.taskDate] - De dag waarop dit item in de agenda staat
 * @param {() => void} [props.onSaved] - Aangeroepen na een geslaagde opslag
 */
const PresenceReportAdjuster = ({
  scheduleId = "",
  personaId,
  scheduledStart,
  scheduledEnd,
  taskDate,
  onSaved,
}) => {
  const [auth] = useContext(Auth);
  const history = useHistory();

  const [correctStartTime, setCorrectStartTime] = useState("");
  const [correctEndTime, setCorrectEndTime] = useState("");
  const [correctionReason, setCorrectionReason] = useState("");

  const [absentReason, setAbsentReason] = useState("");

  const [correctionModalOpen, setCorrectionModalOpen] = useState(false);
  const [absentModalOpen, setAbsentModalOpen] = useState(false);

  const presenceReports = useFirestoreGeneralOrderBy(
    "PersonaPresenceReports",
    "PersonaID",
    personaId || "",
    "Position",
    "asc"
  );

  const formatDateToDutchString = (date) => {
    const daysOfWeek = [
      "zondag",
      "maandag",
      "dinsdag",
      "woensdag",
      "donderdag",
      "vrijdag",
      "zaterdag",
    ];
    const monthsOfYear = [
      "januari",
      "februari",
      "maart",
      "april",
      "mei",
      "juni",
      "juli",
      "augustus",
      "september",
      "oktober",
      "november",
      "december",
    ];

    const dayName = daysOfWeek[date.getDay()];
    const day = date.getDate();
    const monthName = monthsOfYear[date.getMonth()];
    const year = date.getFullYear();

    return `${dayName} ${day} ${monthName} ${year}`;
  };

  const reportDate = taskDate || new Date();
  const dateKey = amsterdamDateKey(reportDate);
  const docId = presenceReportDocId(scheduleId, dateKey);

  // Per roosterblok, niet per datum: bij twee blokken op één dag hoort het
  // rapport van de ochtend niet bij de middag.
  const reportForDate = taskDate
    ? findSlotPresenceReport(presenceReports, {
        docId,
        date: formatDateToDutchString(taskDate),
        start: scheduledStart,
      })
    : null;

  /**
   * @param {object} fields - Het rapport zonder de roostersleutel
   * @returns {Promise<unknown>}
   */
  const writeReport = (fields) => {
    const reports = db.collection("PersonaPresenceReports");
    if (!docId) return reports.add(fields);
    return reports.doc(docId).set({ ...fields, ScheduleID: scheduleId, DateKey: dateKey });
  };

  const getScheduledHours = (startTime, endTime) => {
    if (!startTime || !endTime) return 0;

    // Expect "HH:mm"
    const start = new Date(`1970-01-01T${startTime}:00`);
    const end = new Date(`1970-01-01T${endTime}:00`);
    const diff = end - start;

    return diff / 1000 / 60 / 60;
  };

  const getNextPositionForPersona = async (personaIdArg) => {
    const snap = await db
      .collection("PersonaPresenceReports")
      .where("CompagnyID", "==", client)
      .where("PersonaID", "==", personaIdArg)
      .orderBy("Position", "desc")
      .limit(1)
      .get();

    if (snap.empty) return 1;

    const last = snap.docs[0].data();
    return (last?.Position || 0) + 1;
  };

  const saveCorrection = async () => {
    if (!personaId) return;

    const position = await getNextPositionForPersona(personaId);

    // AI-CONTEXT: See docs/pitfalls.md#state-stale-module-level-timestamp
    await writeReport({
      PersonaID: personaId,
      Start: scheduledStart || "",
      End: scheduledEnd || "",
      CorrectedStart: correctStartTime || "",
      CorrectedEnd: correctEndTime || "",
      Date: formatDateToDutchString(reportDate),
      Schedule: getScheduledHours(scheduledStart, scheduledEnd),
      CompagnyID: client,
      Timestamp: firebase.firestore.Timestamp.fromDate(reportDate),
      Reason: correctionReason || "",
      ID: uuid(),
      Type: "correction",
      Author: auth?.ID || null,
      Position: position,
    });

    setCorrectionModalOpen(false);
    setCorrectStartTime("");
    setCorrectEndTime("");
    setCorrectionReason("");

    if (onSaved) onSaved();
  };

  const saveAbsent = async () => {
    if (!personaId) return;

    const position = await getNextPositionForPersona(personaId);

    await writeReport({
      PersonaID: personaId,
      Date: formatDateToDutchString(reportDate),
      CompagnyID: client,
      Timestamp: firebase.firestore.Timestamp.fromDate(reportDate),
      ID: uuid(),
      Type: "absent",
      Reason: absentReason || "",
      Author: auth?.ID || null,
      Start: scheduledStart || "",
      End: scheduledEnd || "",
      CorrectedStart: "",
      CorrectedEnd: "",
      Schedule: getScheduledHours(scheduledStart, scheduledEnd),
      Position: position,
    });

    setAbsentModalOpen(false);
    setAbsentReason("");

    if (onSaved) onSaved();
  };

  if (!personaId) return null;

  return (
    <div>
      <ReadOnlyGuard edit="tasks.edit">
      {(canEdit) => (
      <>
      {reportForDate ? (
        <div className="presence-report-detail-output-row">
          <p
            className="presence-report-link"
            onClick={() =>
              history.push(
                `/${client}/personapresencereportdetail/${personaId}/${reportForDate.ID}`
              )
            }
          >
            Bekijk aanwezigheidsdagrapport
          </p>
        </div>
      ) : (
        canEdit && (
        <div id="register-presence-container">
          <div
            className="register-precense-options-container"
            onClick={() => setCorrectionModalOpen(true)}
          >
            <img src={ErasorIcon} alt="erasor" />
            <p>Correctie</p>
          </div>

          <div
            className="register-precense-options-container"
            onClick={() => setAbsentModalOpen(true)}
          >
            <img src={XIcon} alt="x" />
            <p>Afwezig</p>
          </div>
        </div>
        )
      )}

      {/* Correction modal */}
      <Modal
        modalOpen={correctionModalOpen}
        setModalOpen={setCorrectionModalOpen}
        confirm={saveCorrection}
        pageHeader="Correctie"
        content={
          <div>
            <div className="register-presence-time-container">
              <p>Start</p>
              <input
                type="time"
                value={correctStartTime}
                onChange={(e) => setCorrectStartTime(e.target.value)}
              />
            </div>

            <div className="register-presence-time-container">
              <p>Eind</p>
              <input
                type="time"
                value={correctEndTime}
                onChange={(e) => setCorrectEndTime(e.target.value)}
              />
            </div>

            <input
              id="register-presence-reason-correction-input"
              type="text"
              placeholder="Reden correctie"
              value={correctionReason}
              onChange={(e) => setCorrectionReason(e.target.value)}
            />
          </div>
        }
      />

      {/* Absent modal */}
      <Modal
        modalOpen={absentModalOpen}
        setModalOpen={setAbsentModalOpen}
        confirm={saveAbsent}
        pageHeader="Afwezig"
        content={
          <div>
            <p>Reden afwezigheid</p>
            <select
              value={absentReason}
              onChange={(e) => setAbsentReason(e.target.value)}
            >
              <option value="">-- Selecteer reden afwezigheid --</option>
              <option value="ziek">Ziek</option>
              <option value="verlof">Verlof</option>
              <option value="overig">Overig</option>
            </select>
          </div>
        }
      />
      </>
      )}
      </ReadOnlyGuard>
    </div>
  );
};

export default PresenceReportAdjuster;
